"use client";
import { Capacitor, registerPlugin } from "@capacitor/core";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { APP_LINK_HOST } from "@/lib/native/app-links";
import {
  deploymentEnvironment,
  isClientTelemetryDeliveryEnabled,
} from "@/lib/telemetry/environment";
import {
  captureAcquisitionEvent,
  queueDeferredDeepLink,
  readClientAcquisition,
  saveClientAcquisition,
  syncAcquisition,
} from "./client";
import {
  type Acquisition,
  contactMatchingAllowed,
  type MetaAppSignals,
  measurementOn,
  safeDeepLinkPath,
  sanitizeAcquisition,
} from "./shared";

type InstallContext = {
  installation_id: string;
  os_version: string;
  is_first_open: boolean;
  meta_sdk_available?: boolean;
  att_status: Acquisition["att_status"];
};
const bridge = registerPlugin<{
  getInstallContext(): Promise<InstallContext>;
  acknowledgeFirstOpen(): Promise<void>;
  requestTracking(): Promise<{ att_status: Acquisition["att_status"] }>;
  configureMeasurement(options: {
    appId: string;
    clientToken: string;
    consent: boolean;
    userId?: string;
    matching?: MetaMatchingHashes;
    activate: boolean;
  }): Promise<{ enabled: boolean; tracking: boolean }>;
  logConfirmedEvent(options: {
    id: string;
    event_id?: string;
    event: "registration" | "purchase";
    amount?: number;
    currency?: string;
  }): Promise<{ accepted: boolean }>;
  logIntentEvent(options: NativeIntentEvent): Promise<{ accepted: boolean }>;
  /** Meta's deferred App Link for an ad-driven install; resolves once per install. */
  fetchDeferredLink(): Promise<{ url?: string; checked: boolean }>;
  getMeasurementSignals(): Promise<Partial<MetaAppSignals>>;
}>("HiiveAttribution");
type MetaMatchingHashes = Partial<
  Record<"em" | "ph" | "fn" | "ln" | "country", string>
>;
/**
 * Hands the server what it needs to send this device's purchases as app
 * events (install ID, extinfo, and the ad ID only where tracking is allowed).
 * Saved into the acquisition context, which syncs to the server.
 */
async function refreshMeasurementSignals() {
  try {
    const signals = await bridge.getMeasurementSignals();
    const current = readClientAcquisition();
    const next = sanitizeAcquisition({ ...current, meta_app: signals });
    if (JSON.stringify(next.meta_app) !== JSON.stringify(current.meta_app))
      saveClientAcquisition({ ...current, meta_app: next.meta_app });
  } catch {
    /* Older binaries lack the method; the SDK copy still delivers. */
  }
}
export type NativeIntentEvent = {
  event: "view_content" | "initiate_checkout" | "add_payment_info";
  content_type: string;
  content_id: string;
  amount?: number;
  currency?: string;
};
/** Browse/checkout intent; a no-op until the SDK is running (and if opted out). */
export async function logNativeIntentEvent(event: NativeIntentEvent) {
  if (
    !Capacitor.isNativePlatform() ||
    readClientAcquisition().meta_native_measurement !== true ||
    !measurementOn(readClientAcquisition().consent)
  )
    return;
  try {
    await bridge.logIntentEvent(event);
  } catch {
    /* Older binaries lack the method; measurement never breaks the page. */
  }
}
let deferredLinkRequested = false;
/**
 * Routes an ad-driven install straight to the advertised page (e.g. an event).
 * Meta only answers once the SDK runs (first launch, unless opted out); on iOS
 * it also needs ATT to be authorized, otherwise Meta returns nothing.
 */
async function resolveDeferredLink() {
  if (deferredLinkRequested) return;
  deferredLinkRequested = true;
  try {
    const { url } = await bridge.fetchDeferredLink();
    if (!url) return;
    // Only our own universal links: the ad's deep link must be an
    // https://app.hiive.club/... URL, never an arbitrary destination.
    const parsed = new URL(url);
    if (parsed.protocol !== "https:" || parsed.hostname !== APP_LINK_HOST)
      return;
    const path = safeDeepLinkPath(
      `${parsed.pathname}${parsed.search}${parsed.hash}`,
    );
    if (path) queueDeferredDeepLink(path);
  } catch {
    /* Older binaries, no link, or a network failure: stay on the current page. */
  }
}
let setup: Promise<void> | undefined;
let currentCustomer = "";
let configuration = "";
let needsActivation = true;
let lastPoll = 0;
export async function setupNativeAcquisition() {
  if (!Capacitor.isNativePlatform()) return;
  if (setup) return setup;
  setup = (async () => {
    try {
      const context = await bridge.getInstallContext();
      const { App } = await import("@capacitor/app");
      const info = await App.getInfo();
      saveClientAcquisition({
        ...readClientAcquisition(),
        installation_id: context.installation_id,
        meta_sdk_available: context.meta_sdk_available === true,
        native_os_version: context.os_version,
        native_app_version: info.version,
        att_status: context.att_status,
        app_surface:
          Capacitor.getPlatform() === "ios" ? "ios_native" : "android_native",
      });
      if (context.is_first_open && isClientTelemetryDeliveryEnabled()) {
        const captured = captureAcquisitionEvent(
          ANALYTICS_EVENTS.APP_FIRST_OPENED,
          {
            first_open_definition: "first_launch_of_instrumented_binary",
            $insert_id: `first-open:${context.installation_id}`,
          },
        );
        if (captured) await bridge.acknowledgeFirstOpen();
      }
      // Before the SDK is configured, so Meta sees the real answer from the
      // start and a deferred link (which needs ATT) can still be fetched.
      await requestTrackingIfUndecided();
    } catch {
      /* Older store binaries do not have this plugin; never invent install events. */
      // A transient bridge failure must not permanently disable this session.
      setup = undefined;
    }
  })();
  return setup;
}
/**
 * Measurement is on by default, so the only prompt anyone sees is Apple's own
 * ATT dialog, which iOS requires before per-user tracking. iOS shows it at most
 * once; it is skipped when the person has turned measurement off, and outside
 * production builds that have Meta configured.
 */
async function requestTrackingIfUndecided() {
  if (
    Capacitor.getPlatform() !== "ios" ||
    deploymentEnvironment !== "production" ||
    !isClientTelemetryDeliveryEnabled() ||
    !process.env.NEXT_PUBLIC_META_APP_ID ||
    !process.env.NEXT_PUBLIC_META_CLIENT_TOKEN
  )
    return;
  const context = readClientAcquisition();
  if (
    context.att_status !== "notDetermined" ||
    context.meta_sdk_available !== true ||
    !measurementOn(context.consent)
  )
    return;
  try {
    await requestNativeTracking();
  } catch {
    /* Retried on the next foreground if iOS did not present it. */
  }
}
export async function requestNativeTracking() {
  if (Capacitor.getPlatform() !== "ios") return;
  const result = await bridge.requestTracking();
  saveClientAcquisition({
    ...readClientAcquisition(),
    att_status: result.att_status,
  });
}
export async function setupNativeAcquisitionLifecycle(): Promise<() => void> {
  if (!Capacitor.isNativePlatform()) return () => {};
  const { App } = await import("@capacitor/app");
  const listener = await App.addListener("appStateChange", ({ isActive }) => {
    if (!isActive) return;
    void bridge
      .getInstallContext()
      .then((context) => {
        saveClientAcquisition({
          ...readClientAcquisition(),
          installation_id: context.installation_id,
          meta_sdk_available: context.meta_sdk_available === true,
          native_os_version: context.os_version,
          att_status: context.att_status,
        });
        // iOS ignores the request unless the app is active; try again now.
        void requestTrackingIfUndecided();
        needsActivation = true;
        void updateNativeAdvertising(currentCustomer || undefined);
      })
      .catch(() => {});
  });
  return () => {
    void listener.remove();
  };
}
let updateChain = Promise.resolve();
export function updateNativeAdvertising(userId?: string): Promise<void> {
  currentCustomer = userId ?? "";
  updateChain = updateChain
    .catch(() => {})
    .then(() => applyNativeAdvertising());
  return updateChain;
}
async function applyNativeAdvertising() {
  if (
    !Capacitor.isNativePlatform() ||
    deploymentEnvironment !== "production" ||
    !isClientTelemetryDeliveryEnabled()
  )
    return;
  await setupNativeAcquisition();
  const context = readClientAcquisition();
  const appId = process.env.NEXT_PUBLIC_META_APP_ID;
  const clientToken = process.env.NEXT_PUBLIC_META_CLIENT_TOKEN;
  if (!appId || !clientToken || context.meta_sdk_available !== true) return;
  const customer = currentCustomer;
  const consent = measurementOn(context.consent);
  const tracking =
    consent &&
    (Capacitor.getPlatform() !== "ios" || context.att_status === "authorized");
  // Never share a Hiive account identifier with Meta after ATT refusal.
  const userId =
    tracking && customer
      ? Array.from(
          new Uint8Array(
            await crypto.subtle.digest(
              "SHA-256",
              new TextEncoder().encode(customer),
            ),
          ),
        )
          .map((byte) => byte.toString(16).padStart(2, "0"))
          .join("")
      : undefined;
  if (customer !== currentCustomer) return;
  const matching: MetaMatchingHashes = {};
  if (customer && tracking && contactMatchingAllowed(context)) {
    try {
      if (await syncAcquisition()) {
        const response = await fetch("/api/acquisition/matching", {
          cache: "no-store",
          signal: AbortSignal.timeout(5000),
        });
        if (response.ok) {
          const body = await response.json();
          for (const key of ["em", "ph", "fn", "ln", "country"] as const)
            if (
              typeof body.matching?.[key] === "string" &&
              /^[a-f0-9]{64}$/.test(body.matching[key])
            )
              matching[key] = body.matching[key];
        }
      }
    } catch {
      /* Clear matching on failure; never fall back to profile data. */
    }
  }
  if (
    customer !== currentCustomer ||
    context.consent !== readClientAcquisition().consent ||
    context.att_status !== readClientAcquisition().att_status
  )
    return;
  const signature = JSON.stringify([
    appId,
    consent,
    context.att_status,
    userId,
    matching,
  ]);
  if (signature !== configuration || needsActivation) {
    const result = await bridge.configureMeasurement({
      appId,
      clientToken,
      consent,
      userId,
      matching,
      activate: needsActivation && consent,
    });
    const changed = signature !== configuration;
    configuration = signature;
    if (changed)
      captureAcquisitionEvent(ANALYTICS_EVENTS.NATIVE_AD_MEASUREMENT_UPDATED, {
        provider: "meta_sdk",
        sdk_enabled: result.enabled,
        measurement_mode: result.enabled
          ? result.tracking
            ? "tracking_authorized"
            : "limited"
          : "disabled",
        native_campaign_mapping: "not_exposed_by_sdk",
      });
    if (result.enabled) {
      needsActivation = false;
      void resolveDeferredLink();
      // Complete the device snapshot before syncing it or polling purchases.
      // Fire-and-forget here raced the first authenticated sync, leaving the
      // server with an app surface but no signals to deliver its app event.
      await refreshMeasurementSignals();
    }
    const current = readClientAcquisition();
    if (current.meta_native_measurement !== result.enabled)
      saveClientAcquisition({
        ...current,
        meta_native_measurement: result.enabled,
      });
  }
  // Retry a failed/empty bridge response even when configuration is unchanged.
  if (consent && !readClientAcquisition().meta_app)
    await refreshMeasurementSignals();
  if (
    !consent ||
    !customer ||
    !context.installation_id ||
    Date.now() - lastPoll < 5000
  )
    return;
  if (!(await syncAcquisition())) return;
  if (
    customer !== currentCustomer ||
    !measurementOn(readClientAcquisition().consent)
  )
    return;
  lastPoll = Date.now();
  let body: unknown;
  try {
    const response = await fetch("/api/acquisition/native-events", {
      cache: "no-store",
    });
    if (!response.ok) return;
    body = await response.json();
  } catch {
    // Measurement polling is optional. A dropped connection must not reject
    // the navigation/consent flow that triggered this check.
    return;
  }
  if (!body || typeof body !== "object") return;
  if (!("events" in body) || !Array.isArray(body.events)) return;
  const ids: string[] = [];
  for (const event of body.events.slice(0, 20)) {
    if (
      customer !== currentCustomer ||
      !measurementOn(readClientAcquisition().consent)
    )
      break;
    if (
      typeof event.id !== "string" ||
      !["registration", "purchase"].includes(event.event)
    )
      continue;
    // Only authoritative server facts enter the SDK. It owns Apple conversion
    // value updates, limited-measurement mode, install reporting, and retries.
    const result = await bridge.logConfirmedEvent(event);
    if (result.accepted) ids.push(event.id);
  }
  if (
    ids.length &&
    customer === currentCustomer &&
    measurementOn(readClientAcquisition().consent)
  )
    await fetch("/api/acquisition/native-events", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ ids }),
      keepalive: true,
    });
}
